import { MotionConfig } from 'framer-motion';
import { Script } from 'gatsby';
import { useMixpanel } from 'gatsby-plugin-mixpanel';
import React, { ReactElement, useEffect } from 'react';
import Meta from '../../meta';
import Footer from './partials/footer';
import Navigation from './partials/navigation';
import './style.css';

interface IProps {
  children: any;
  pageBgColor?: string;
  navBgColor?: string;
  hidePrivPolicy?: boolean;
  hideNavigation?: boolean;
  hideFooter?: boolean;
  className?: string;
  style?: any;
}

declare global {
  interface Window {
    Cookiebot: any;
  }
}

// The main template that every other template/page should use.
// It contains our nav, footer, and lots of other things that are
// necessary like our cookie consent.
export default function GlobalTemplate({
  children,
  pageBgColor,
  navBgColor,
  hideNavigation,
  hideFooter,
  className = '',
  style,
}: IProps): ReactElement {
  const mixpanel = useMixpanel();
  let Cookiebot;

  if (typeof window !== `undefined`) {
    Cookiebot = window.Cookiebot;
  }

  const mixpanelOptInOptOut = () => {
    if (Cookiebot === undefined) {
      setTimeout(() => mixpanelOptInOptOut(), 500);
    } else {
      if (
        !Cookiebot ||
        !Cookiebot.consent ||
        (!Cookiebot.consent.statistics && !mixpanel.has_opted_out_tracking())
      ) {
        // Opt out if Cookiebot consent has not been given and user is not already opted out
        mixpanel.opt_out_tracking();
      } else if (
        !!Cookiebot &&
        !!Cookiebot.consent &&
        !!Cookiebot.consent.statistics &&
        !mixpanel.has_opted_in_tracking()
      ) {
        // Opt in if Cookiebot consent is given and user is not already opted in
        mixpanel.opt_in_tracking();
      }
      // Otherwise, no action is taken
    }
  };

  useEffect(() => {
    mixpanelOptInOptOut();
  }, []);

  useEffect(() => {
    window.addEventListener('CookiebotOnAccept', mixpanelOptInOptOut);

    return () => {
      window.removeEventListener('CookiebotOnAccept', mixpanelOptInOptOut);
    };
  });

  return (
    <MotionConfig reducedMotion="user">
      {pageBgColor && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
          body { background-color: var(--color-base-${pageBgColor}) }
        `,
          }}
        />
      )}

      <Meta>
        <meta name="author" content="Stark Lab, Inc" />

        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="icon" href="/icon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/manifest.webmanifest" />

        <script
          id="CookiebotScript"
          src="https://consent.cookiebot.com/uc.js"
          data-cbid="e24782ce-82b5-45a8-b286-6a989f42e872"
          data-blockingmode="auto"
          type="text/javascript"
          async
        />
      </Meta>

      <div
        className={`layout overflow-hidden ${className}`}
        style={{ ...style }}>
        {!hideNavigation && (
          <div className={`flex justify-center p-4 bg-base-${navBgColor}`}>
            <div className="w-full max-w-[960px]">
              <Navigation bgColor={navBgColor} />
            </div>
          </div>
        )}

        {children}
      </div>

      {!hideFooter && <Footer />}
      <Script>
        {`(function (c, p, d, u, id, i) {
  id = ''; // Optional Custom ID for user in your system
  u = 'https://tracking.g2crowd.com/attribution_tracking/conversions/' + c + '.js?p=' + encodeURI(p) + '&e=' + id;
  i = document.createElement('script');
  i.type = 'application/javascript';
  i.async = true;
  i.src = u;
  d.getElementsByTagName('head')[0].appendChild(i);
}("1009514", document.location.href, document));`}
      </Script>
    </MotionConfig>
  );
}
